import { NgModule } from '@angular/core';
import {
  AuthModule,
  AbstractSecurityStorage,
  OpenIdConfiguration,
  LogLevel,
  StsConfigLoader,
  StsConfigHttpLoader,
} from 'angular-auth-oidc-client';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { CustomStorageService } from './core/services/custom-storage.service';
import { resolveAuth0Enabled } from './auth/resolve-auth0-enabled';

/**
 * Shape of the `environment.auth0Config` block every real environment file carries. Deliberately
 * has no `audience` field — see auth0AuthConfig()'s comment for why this SPA never requests one.
 */
interface Auth0Config {
  authority: string;
  clientId: string;
  scope?: string;
  redirectUrl: string;
  postLogoutRedirectUri: string;
  secureRoutes: string[];
  logLevel?: LogLevel;
  /**
   * Fallback Auth0 connection for SSO logins whose SSO-preference lookup returns no
   * saml_connection_name — notably Microsoft/Azure AD, where the connection is a tenant-wide
   * Enterprise Connection rather than a per-customer value (SYRC-6097). Enterprise/SAML customers
   * carry their own connection in the API response and do not need this.
   */
  defaultSsoConnection?: string;
}

/**
 * The identity providers this SPA knows about.
 *
 * Which one is active is NOT hard-set in the environment files — it is resolved from the ConfigCat
 * flag `corinsights_auth0_enabled` (see resolve-auth0-enabled.ts), the single source of truth
 * shared with the backend authorizer and API. Read once, lazily, by authConfigLoaderFactory()'s
 * StsConfigLoader — see its comment for why this can safely be async instead of frozen pre-bootstrap.
 */
export type AuthProvider = 'corview' | 'auth0';

/** The provider assumed when the flag resolves false, times out, or errors. */
export const DEFAULT_AUTH_PROVIDER: AuthProvider = 'corview';

/**
 * Today's config, unchanged. Every field is read from environment.authConfig exactly as the previous
 * static AuthModule.forRoot({config: {...}}) literal did — this path must stay behaviourally
 * identical, since it is the one every real environment uses.
 */
function corviewAuthConfig(): OpenIdConfiguration {
  return {
    authority: environment.authConfig.authority,
    redirectUrl: environment.authConfig.redirectUrl,
    postLogoutRedirectUri: environment.authConfig.postLogoutRedirectUri,
    clientId: environment.authConfig.clientId,
    scope: environment.authConfig.scope,
    responseType: environment.authConfig.responseType,
    startCheckSession: environment.authConfig.startCheckSession,
    silentRenew: environment.authConfig.silentRenew,
    useRefreshToken: environment.authConfig.useRefreshToken,
    ignoreNonceAfterRefresh: environment.authConfig.ignoreNonceAfterRefresh,
    renewTimeBeforeTokenExpiresInSeconds:
      environment.authConfig.renewTimeBeforeTokenExpiresInSeconds,
    logLevel: environment.authConfig.logLevel,
    historyCleanupOff: environment.authConfig.historyCleanupOff,
    secureRoutes: environment.authConfig.secureRoutes,
    maxIdTokenIatOffsetAllowedInSeconds:
      environment.authConfig.maxIdTokenIatOffsetAllowedInSeconds,
  };
}

/**
 * Auth0 OIDC configuration.
 *
 * Key differences from the CorView STS path:
 *
 * 1. Deliberately NO custom API audience (customParamsAuthRequest.audience) — this mirrors the
 *    CorView path exactly rather than corviewng's REST-API pattern, because the two SPAs send
 *    different tokens to their backends. corviewng's angular-auth-oidc-client AuthInterceptor
 *    auto-attaches the ACCESS token to REST calls matched by secureRoutes, so it needs a custom
 *    audience to make that access token a verifiable JWT. syrc_web instead sends the ID token to
 *    AppSync explicitly (BaseAWSRequestService: `authToken: this.idToken`, never the access token,
 *    no branching by provider) — and an id_token's `aud` is ALWAYS the requesting client id, per
 *    OIDC, regardless of any audience requested at /authorize. Requesting one here would do nothing
 *    for the token actually validated, while costing a real failure mode: Auth0 rejects /authorize
 *    outright (before login even renders) if the client lacks a Client Grant for that audience.
 *    validateToken.mjs's AUTH0_AUDIENCE is configured to the Auth0 client id for exactly this
 *    reason — the same shape as CorView's own SYRC_WEB_JWT_AUDIENCE (`syrcDashboard`, its client id).
 *
 * 2. `silentRenew: false` + `useRefreshToken: true` — same renewal shape as the CorView path.
 *    TokenRenewalCoordinatorService is the sole renewer for every provider: it owns a cross-tab Web
 *    Lock so only one tab redeems the shared refresh token at a time. Setting silentRenew: true here
 *    would additionally arm angular-auth-oidc-client's own per-tab renewal timer (it uses the
 *    refresh-token strategy whenever useRefreshToken is true), which would then race the coordinator
 *    for the same refresh token — the exact multi-tab collision (one tab wins, the rest get a 400
 *    invalid_grant) TokenRenewalCoordinatorService's Web Lock exists to prevent. See its own comment.
 *
 * 3. `scope` includes 'email' and 'offline_access' — 'email' ensures the email claim is in the ID
 *    token (used for SSO routing). 'offline_access' requests a refresh token from Auth0.
 *
 * 4. `postLogoutRedirectUri` points to /login — the new login landing page per SYRC-6097.
 *
 * Claim compatibility: Auth0 ID tokens include `sub` (with provider prefix, e.g. `auth0|<ulid>`)
 * and `given_name` by default. The SYRC user id is resolved by resolveTokenUserId() in the profile
 * bootstrap path (auth-return.component.ts / profile-store.service.ts / auth.guard.ts): it prefers
 * the `user_id` custom claim (injected by the Auth0 post-login Action, SYRC-7268) so federated SSO
 * users resolve correctly, and only falls back to stripping the `sub` prefix for database users.
 * No claim mapping is needed here.
 */
function auth0AuthConfig(): OpenIdConfiguration {
  const auth0 = (environment as { auth0Config?: Auth0Config }).auth0Config;
  if (!auth0) {
    throw new Error(
      "authProvider is 'auth0' but environment.auth0Config is not defined. " +
      'Each environment file must include an auth0Config block with authority, clientId, ' +
      'scope, redirectUrl, postLogoutRedirectUri, and secureRoutes.',
    );
  }

  return {
    // Strip any trailing slash before use: angular-auth-oidc-client's well-known lookup does a
    // naive string concat (authority + '/.well-known/openid-configuration', no slash
    // normalisation) — a trailing slash here produces a double slash in the discovery URL, which
    // Auth0 serves without CORS headers, so the browser reports it as a CORS failure rather than a
    // 404. Defensive here rather than relying on every environment file being typed correctly.
    authority: auth0.authority.replace(/\/+$/, ''),
    redirectUrl: auth0.redirectUrl,
    postLogoutRedirectUri: auth0.postLogoutRedirectUri,
    clientId: auth0.clientId,
    scope: auth0.scope ?? 'openid profile email offline_access',
    responseType: 'code',
    startCheckSession: false,
    silentRenew: false,
    useRefreshToken: true,
    ignoreNonceAfterRefresh: true,
    renewTimeBeforeTokenExpiresInSeconds: 300,
    logLevel: auth0.logLevel ?? environment.authConfig?.logLevel,
    historyCleanupOff: true,
    secureRoutes: auth0.secureRoutes,
    maxIdTokenIatOffsetAllowedInSeconds: 120,
  };
}

/**
 * Resolves the OIDC config for an explicit provider.
 *
 * Fails LOUDLY when 'auth0' is selected without an `auth0Config` block rather than silently falling
 * back to the corview config: a silent fallback would produce a SPA that believes it is in Auth0
 * mode while actually driving the CorView STS, which is precisely the split-brain state this seam
 * exists to make impossible.
 */
export function resolveAuthConfig(provider: AuthProvider): OpenIdConfiguration {
  switch (provider) {
    case 'corview':
      return corviewAuthConfig();

    case 'auth0':
      return auth0AuthConfig();

    default:
      throw new Error(
        `Unknown authProvider '${provider}': expected 'corview' or 'auth0'.`,
      );
  }
}

/**
 * Builds the StsConfigLoader AuthModule.forRoot() uses instead of a literal config.
 *
 * WHY A LOADER, NOT A LITERAL {config: ...}. A literal config is built eagerly, at
 * AuthConfigModule's own @NgModule decorator-evaluation time — i.e. as soon as this file is
 * imported, before Angular's injector (and therefore ConfigCat/OpenFeature) exists. A
 * `StsConfigLoader` instead registers a PROVIDER RECIPE; Angular doesn't invoke this factory until
 * something actually needs the OIDC config (e.g. the app's first checkAuth() call), which happens
 * well after bootstrapModule() has finished — so resolveAuth0Enabled() can safely call
 * OpenFeature.getClient() here without any pre-bootstrap freezing step in main.ts.
 *
 * The Observable resolves exactly once: resolveAuth0Enabled() itself only ever settles a single
 * time (see its own comment), and `from(promise)` completes after that one emission, so
 * StsConfigHttpLoader's internal concatMap can't act on a stale value followed by a real one.
 */
export function authConfigLoaderFactory(): StsConfigLoader {
  const config$ = from(resolveAuth0Enabled(false)).pipe(
    map((isAuth0Enabled) => resolveAuthConfig(isAuth0Enabled ? 'auth0' : DEFAULT_AUTH_PROVIDER)),
  );
  return new StsConfigHttpLoader(config$);
}

@NgModule({
  imports: [
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: authConfigLoaderFactory,
      },
    }),
  ],
  providers: [
    {
      provide: AbstractSecurityStorage,
      useClass: CustomStorageService,
    },
  ],
  exports: [AuthModule],
})
export class AuthConfigModule {}
